import "react";

export const SKIP_VALUE = "__skipquestion__";

export type ObjectId = string;
export type StoredObject = {
  _id: ObjectId;
  createdAt: string;
  updatedAt: string;
};

export type Stored<T> = StoredObject & T;
export type MaybeStored<T> = Partial<StoredObject> & T;

export enum SessionStatus {
  Draft = "draft",
  Ongoing = "ongoing",
  Results = "results",
}

export enum SessionTempo {
  Teacher = "teacher",
  Student = "student",
}

export enum StaticPage {
  Home = "home",
  About = "about",
  Privacy = "privacy",
  UserGuide = "user-guide",
}

export enum ContentBlockType {
  ImageWithDescription = "image_with_description",
  TitleWithDescription = "title_with_description",
  BigImage = "big_image",
}

export enum UserRole {
  TEACHER = "teacher",
  ADMIN = "admin",
  SUPER_ADMIN = "super_admin",
}

export type VotingSession = {
  name: string;
  code: string;
  type: "debate";
  tempo: SessionTempo;
  state: SessionStatus;
  argumentsEnabled: boolean;
  skipQuestions: boolean;
  currentStatementIndex: number;
  statements: Array<ObjectId>;
  showResults: boolean;
};

export type OpinionOption = { opinion: string; order: number; color: string };

export type Statement = {
  description: string;
  opinionOptions: Array<OpinionOption>;
};

export type StudentOpinion = {
  statementId: ObjectId;
  sessionId: ObjectId;
  opinion: string;
  studentId: string;
  comment?: string;
  hidden: boolean;
};

export type Party = {
  name: string;
  description: string;
  hasLogo: boolean;
};

export type PartyOpinion = {
  statementId: ObjectId;
  partyId: ObjectId;
  opinion: string;
  comment: string;
};

export type DifficultWord = {
  word: string;
  description: string;
};

export type Partner = {
  name: string;
  website: string;
};

export type ContentBlock = {
  legacy?: string;
  page: StaticPage;
  type: ContentBlockType;
  html?: string;
  order?: number;
  title: string;
  imageURL?: string;
  imageAlt?: string;
  body: string;
  inverted: boolean;
};

export type GameTemplate = {
  name: string;
  type: "debate";
  parties: Array<ObjectId>;
  statements: Array<ObjectId>;
  skipQuestions: boolean;
  ownArguments: boolean;
  ownTempo: boolean;
  createdBy: ObjectId;
  deleted: boolean;
  questionSet: ObjectId;
  default: boolean;
};

export type QuestionSet = {
  name: string;
  description?: string;
  published: boolean;
  statements: Array<ObjectId>;
  parties: Array<ObjectId>;
  hidden: boolean;
};

export type User = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: UserRole;
  organisation: string;
  organisationLocation: string;
  activated: boolean;
  targetAudience: string;
};

export type PopulatedWithParties<T extends { parties: Array<ObjectId> }> = Omit<
  T,
  "parties"
> & { parties: Array<Stored<Party>> };
export type PopulatedWithStatements<T extends { statements: Array<ObjectId> }> =
  Omit<T, "statements"> & { statements: Array<Stored<Statement>> };
export type PopulatedVotingSession = Stored<
  PopulatedWithStatements<VotingSession>
> & {
  studentOpinions: Array<Stored<StudentOpinion>>;
};

export type PopulatePartyOpinion = Stored<Omit<PartyOpinion, "partyId">> & {
  partyId: Stored<Party>;
};

declare module "react" {
  interface CSSProperties {
    // Allow any CSS variable starting with '--'
    [key: `--${string}`]: string | number;
  }
}
